import moment from "moment";
import { z } from "zod";
import axios from "axios";
import { api, SocketStatus } from ".";
import { AppThunk, EVENT_SOCKET_STATUS } from "../store";
import { ChatUserUpdated, DeleteMessageReactionsParams, NebulaChatSocket, SendMessageReactionParams, UserTyping } from "./NebulaChatSocket";
import { ChunkType, } from "../../types";
import { useTypedSelector } from "../hooks";
import { selectChatterByUuid, selectCurrentUserId, setActiveChat } from "../slices";
import { CACHE_CHAT_ADDR, nebulaServerOrDefault } from "./neulaApi";
import { selectActiveConversation } from "../slices/chatNSlice";

const NEBULA_CHAT_URL = nebulaServerOrDefault(CACHE_CHAT_ADDR, true);

const chatGroupTypeSchema = z.enum(["user", "channel", "thread", "integration"]);
export type ChatGroupType = z.infer<typeof chatGroupTypeSchema>;

const messageStatusSchema = z.enum(["delivered", "read", "star", "unstar"]);
export type MessageStatus = z.infer<typeof messageStatusSchema>;

export interface NebulaMessagesRequest { uuid: string; type: ChatGroupType; limit?: number; message_ref: MessageRef | null; chunk: ChunkType };

const channelMemberSchema = z.object({
    uuid: z.string(),
    user_uuid: z.string(),
    muted: z.boolean(),
    muted_desktop: z.boolean(),
    joined_at: z.string(),
});
export type ChannelMember = z.infer<typeof channelMemberSchema>;

export const chatMemberSchema = z.object({
    uuid: z.string(),
    member: z.string(),
    external: z.boolean(),
    joined_at: z.string(),
    last_read_at: z.string().nullish(),
    last_delivered_at: z.string().nullish(),
});
export type ChatMember = z.infer<typeof chatMemberSchema> & {
    hidden?: boolean;
};

const chatterSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    is_active: z.boolean(),
    file_key: z.string().nullish(),
})
export type Chatter = z.infer<typeof chatterSchema> & {
    sip_name?: string,
    is_integration?: boolean
};

const integrationDataSchema = z.object({
    uuid: z.string(),
    destination: z.string(),
    channel: z.string(),
    nickname: z.string().nullish(),
});
export type IntegrationDestination = z.infer<typeof integrationDataSchema>;

const channelSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    description: z.string(),
    private: z.boolean(),
    created_by: z.string(),
    created_at: z.string(),
    updated_at: z.string().nullable().optional(),
    members: z.array(channelMemberSchema),
    muted: z.boolean(),
    muted_desktop: z.boolean(),
    avatar_set: z.boolean(),
    is_joined: z.boolean(),
});
export type Channel = z.infer<typeof channelSchema>;

const messageFileSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    content_type: z.string(),
    size: z.number(),
    shared_by: z.string(),
    shared_at: z.string(),
    metadata: z.record(z.any()).nullable().optional(),
    pending: z.boolean(),
});
export type MessageFile = z.infer<typeof messageFileSchema>;

const messageReactionsSchema = z.object({
    uuid: z.string(),
    content: z.string(),
    time: z.string(),
    user: z.string(),
});
export type MessageReactions = z.infer<typeof messageReactionsSchema>;

export const chatMessageSchema = z.object({
    uuid: z.string(),
    from: z.string(),
    from_type: z.string(),
    to: z.string(),
    to_type: z.string(),
    time: z.string(),
    content: z.string().nullable().optional(),
    deleted_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
    reply_to: z.string().nullable().optional(),
    files: z.array(messageFileSchema).nullish(),
    reactions: z.array(messageReactionsSchema).nullish(),
    has_thread: z.boolean(),
});
export type ChatMessage = z.infer<typeof chatMessageSchema>;

const nebulaIntegrationSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    description: z.string(),
});
export type NebulaChatIntegration = z.infer<typeof nebulaIntegrationSchema>


export const lastMessageSchema = z.object({
    uuid: z.string(),
    type: z.string(),
    created_by: z.string(),
    created_at: z.string(),
    unread: z.number(),
    last_message: z.string().nullable().optional(),
    last_message_at: z.string().nullable().optional(),
    last_message_from: z.string().nullable().optional(),
    last_message_uuid: z.string().nullable().optional(),
    message: chatMessageSchema.nullable().optional(),
    parent_message: chatMessageSchema.nullable().optional(),
    integration: nebulaIntegrationSchema.nullish(),
    avatar_set: z.boolean(),
    display_name: z.string(),
    chat_updated_at: z.string().nullable().optional(),
    is_muted: z.boolean(),
    is_favourite: z.boolean(),
    is_archived: z.boolean(),
});
export type LastMessage = z.infer<typeof lastMessageSchema>;

export interface Conversation extends LastMessage {
    is_search?: boolean
}

const friendRequestSchema = z.object({
    uuid: z.string(),
    from: z.string(),
    to: z.string(),
    message: z.string(),
    created_at: z.string(),
    from_display_name: z.string().nullable().optional(),
});
export type FriendRequest = z.infer<typeof friendRequestSchema>;

const friendSchema = z.object({
    uuid: z.string(),
    user_uuid: z.string(),
    display_name: z.string(),
});
export type Friend = z.infer<typeof friendSchema>;

const fileResponseSchema = z.object({
    uuid: z.string(),
    message: z.string(),
    index: z.number(),
    group: z.string(),
    group_type: z.string(),
    shared_by: z.string(),
    shared_at: z.string(),
    name: z.string(),
    content_type: z.string(),
    size: z.number(),
    status: z.string(),
    metadata: z.any().optional(),
});
export type FileResponse = z.infer<typeof fileResponseSchema>;

const pendingFileInfoSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    content_type: z.string(),
    size: z.number(),
    status: z.string(),
});
export type PendingFileInfo = z.infer<typeof pendingFileInfoSchema>;

export const reactionSchema = z.object({
    uuid: z.string(),
    content: z.string(),
    time: z.string(),
    user: z.string(),
});
export type Reaction = z.infer<typeof reactionSchema>;

const threadMemberSchema = z.object({
    uuid: z.string(),
    user_uuid: z.string(),
    joined_at: z.string(),
});
export type ThreadMember = z.infer<typeof threadMemberSchema>;

const uploadFileResponseSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    content_type: z.string(),
    size: z.number(),
    status: z.string(),
});
export type UploadFileResponse = z.infer<typeof uploadFileResponseSchema>;

// Response schemas
const getChannelsResponseSchema = z.object({
    channels: z.array(channelSchema),
});

const getMessagesResponseSchema = z.object({
    messages: z.array(chatMessageSchema),
    has_more: z.boolean(),
});

const getMessageResponseSchema = z.object({
    message: chatMessageSchema,
});

const sendMessageResponseSchema = z.object({
    message: chatMessageSchema,
    pending_files: z.array(pendingFileInfoSchema).nullable().optional(),
});

const getLastMessagesResponseSchema = z.object({
    conversations: z.array(lastMessageSchema),
    has_more: z.boolean(),
    next_cursor: z.string().nullable().optional(),
});

const getFilesResponseSchema = z.object({
    files: z.array(fileResponseSchema),
    has_more: z.boolean(),
    next_cursor: z.string().nullable().optional(),
});

const getStarredResponseSchema = z.object({
    messages: z.array(chatMessageSchema),
});

const searchMessagesResponseSchema = z.object({
    messages: z.array(chatMessageSchema),
    next_cursor: z.string().nullish(),
});

const threadCountResponseSchema = z.object({
    message_uuid: z.string(),
    count: z.number(),
});

const threadLastMessageResponseSchema = z.object({
    message_uuid: z.string(),
    last_message: chatMessageSchema.nullable().optional(),
});

const getThreadMembersResponseSchema = z.object({
    message_uuid: z.string(),
    members: z.array(threadMemberSchema),
});

const messageStatusResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().nullable().optional(),
});

const readAllResponseSchema = z.object({
    success: z.boolean(),
});

export interface SendMessageRequest {
    to: string;
    to_type: ChatGroupType | string;
    content?: string | null;
    reply_to?: string | null;
    files?: {
        name: string;
        content_type: string;
        size: number
    }[]
};

export type ChannelAction = ({
    action: "join" | "leave",
    users?: string[]
} | {
    action: "invite_members" | "remove_members",
    users: string[]
}) & {
    channel_uuid: string
}

let SOCKET: NebulaChatSocket | undefined;
let LAST_FETCH_MS: number | undefined;

export const killConversationSocket = (reason: string) => {
    SOCKET?.close(4001, reason);

    switch (reason) {
        // if user is logging out OR switching account
        // close the websocket and then invalidate token and websocket
        case 'switch-user':
        case 'logout':
        case 'reset_user':
        case 'reset_chat':
            LAST_FETCH_MS = undefined;
            SOCKET = undefined;
            window.dispatchEvent(new Event(EVENT_SOCKET_STATUS));
            break;

        // if client loses connection to the internet
        // only close the websocket AND don't invalidate
        case 'offline':
            break;
    }
};

let LAST_FETCH_LOCK = false;

const fetchLastMessagesSinceConnect = (): AppThunk<Promise<void>> => async (dispatch) => {
    if (LAST_FETCH_LOCK) return;
    LAST_FETCH_LOCK = true;

    const after = LAST_FETCH_MS !== undefined
        ? `0_${LAST_FETCH_MS}000_admin`
        : undefined;

    const fetchAt = Date.now();

    try {
        await Promise.all([
            dispatch(nebulaChatApi.endpoints.getLastMessagesN.initiate({ limit: 500, type: ['user', 'integration', 'channel'], after }, { forceRefetch: true })).unwrap(),
            dispatch(nebulaChatApi.endpoints.getLastMessagesN.initiate({ limit: 500, type: ['thread'], after }, { forceRefetch: true })).unwrap(),
        ]);

        // Only advance the cursor once both fetches actually succeed. A failed
        // catch-up therefore retries the same window on the next reconnect
        // instead of poisoning LAST_FETCH_MS and permanently skipping the
        // messages it never got.
        LAST_FETCH_MS = fetchAt;
    } catch (e) {
        console.error("Chat catch-up fetch failed; keeping previous cursor", e);
    } finally {
        LAST_FETCH_LOCK = false;
    }
};

export const checkConversationSocket = (): SocketStatus => {
    if (!SOCKET) return 'ok';

    return SOCKET.check_connection_status();
}

export const reconnectConversationSocket = () => {
    if (!SOCKET) return;
    SOCKET.connect();
}

const buildConversationSocket = (
    { userName, password, userUuid }: { userName: string; password: string; userUuid: string }
): AppThunk<Promise<boolean>> => async (dispatch) => {
    if (SOCKET) {
        SOCKET.close(4001, 'superseded');
    }

    const newSocket = new NebulaChatSocket(userName, password);
    // Assign early so checkConversationSocket reflects the connecting state
    SOCKET = newSocket;
    try {
        await newSocket.start(dispatch, userUuid, () => {
            dispatch(fetchLastMessagesSinceConnect());
        });

        dispatch(nebulaChatApi.endpoints.getChatFriendDetails.initiate({ voip_user_uuid: userUuid }));

        window.dispatchEvent(new Event(EVENT_SOCKET_STATUS));
        return true;
    } catch (ex) {
        console.error("CONNECT FAILED", ex);
        window.dispatchEvent(new Event(EVENT_SOCKET_STATUS));
        throw ex;
    }
};

export const recoverConversationSocket = (): AppThunk => (dispatch, getState) => {
    if (!SOCKET) return;
    const { name, password, uuid } = getState().user;
    if (!name || !password || !uuid) return;
    SOCKET.close(4001, 'recover');
    dispatch(buildConversationSocket({ userName: name, password, userUuid: uuid }))
        .catch(() => { /* already logged; controller retries */ });
};

function makeZodResponse<T extends z.ZodTypeAny>(result: T) {
    return z.object({
        status_code: z.number(),
        result,
    });
}

type NebulaRespose<T> = { data: T, error?: never } | { data?: never, error: { status?: number; data?: unknown } };

export const useChatChatter = ({ userUuid }: { userUuid?: string }): Chatter => {
    const chatterFromStore = useTypedSelector(state => selectChatterByUuid(state, userUuid));

    const chatter = nebulaChatApi.endpoints.getChatUserDetails.useQuery(
        { user_uuid: userUuid || "" },
        { skip: !!chatterFromStore || !userUuid }
    );

    return chatterFromStore ?? chatter.data ?? {
        uuid: "__deleted__",
        name: "Deleted User",
        is_active: false,
        file_key: null,
    };
}

export interface MessageRef {
    uuid: string,
    time: string,
    is_before: boolean
};

function cursor_from_message_ref(message_ref: MessageRef | null): { before: string | null, after: string | null } {
    if (!message_ref) {
        return { before: null, after: null }
    }
    let time_ms = moment(message_ref.time).valueOf();
    if (message_ref.is_before) {
        time_ms += 1;
    }

    // Cursor is µs_uuid
    const cursor = `${time_ms}000_${message_ref.uuid}`;

    return {
        before: message_ref.is_before ? cursor : null,
        after: !message_ref.is_before ? cursor : null,
    }
}

async function nebulaFetchBlob(config: {
    url: string;
    params?: Record<string, unknown>;
    headers?: Record<string, string>;
    contentType?: string;
}): Promise<NebulaRespose<string>> {
    try {
        if (!SOCKET) {
            return { error: { data: "Socket Not connected" } };
        }

        const token = await SOCKET.getAuthToken();

        const result = await axios({
            method: 'GET',
            url: `${NEBULA_CHAT_URL}${config.url}`,
            params: config.params,
            responseType: 'blob',
            headers: {
                ...config.headers,
                Authorization: `Bearer ${token}`,
            },
        });

        const blob = config.contentType
            ? new Blob([result.data], { type: config.contentType })
            : result.data;

        return { data: URL.createObjectURL(blob) };
    } catch (err: unknown) {
        const axiosError = err as { response?: { status?: number; data?: unknown } };
        return { error: { status: axiosError.response?.status, data: axiosError.response?.data } };
    }
}

async function nebulaFetch<T>(config: {
    method: string;
    url: string;
    params?: Record<string, unknown>;
    data?: unknown;
    headers?: Record<string, string>;
}): Promise<NebulaRespose<T>> {
    try {
        if (!SOCKET) {
            return {
                error: { data: "Socket Not connected" },
            };
        }

        const token = await SOCKET.getAuthToken();

        const result = await axios({
            method: config.method,
            url: `${NEBULA_CHAT_URL}${config.url}`,
            params: config.params,
            data: config.data,
            headers: {
                ...config.headers,
                Authorization: `Bearer ${token}`,
            },
        });

        return { data: result.data };
    } catch (err: unknown) {
        const axiosError = err as { response?: { status?: number; data?: unknown } };
        return {
            error: { status: axiosError.response?.status, data: axiosError.response?.data },
        };
    }
}

export const nebulaChatApi = api.injectEndpoints({
    endpoints: (builder) => ({
        connectNebulaChat: builder.query<boolean, { userName: string, password: string, userUuid: string }>({
            queryFn: async (args, { dispatch }) => {
                try {
                    await dispatch(buildConversationSocket(args));
                    return { data: true };
                } catch (ex) {
                    return { error: { data: ex } };
                }
            },
        }),
        // Channels
        getChannelsN: builder.query<Channel[], { type: 'public' | 'joined'; include_empty?: boolean }>({
            providesTags: ['nebula_chat'],
            queryFn: async ({ type, include_empty }) => {
                const result = await nebulaFetch<{ result: { channels: Channel[] } }>({
                    method: "GET",
                    url: "/v1/chat/channels",
                    params: { type, include_empty },
                });
                const shape = makeZodResponse(getChannelsResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: [] };
                return { data: validated.data.result.channels };
            },
        }),

        createChannelN: builder.mutation<Conversation | null, { name: string; description?: string; private?: boolean; members: string[] }>({
            queryFn: async (data) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "POST",
                    url: "/v1/chat/channels",
                    data,
                });
                const validated = lastMessageSchema.safeParse(result.data);

                if (!validated.success) return { data: null };
                return { data: validated.data };
            },
        }),

        getChannelN: builder.query<Channel | null, string>({
            providesTags: ['nebula_chat'],
            queryFn: async (channel_uuid) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "GET",
                    url: `/v1/chat/channels/${channel_uuid}`,
                });
                const shape = makeZodResponse(channelSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: null };
                return { data: validated.data.result };
            },
        }),

        updateChannelN: builder.mutation<Channel | null, { channel_uuid: string; name: string; description?: string; private?: boolean }>({
            queryFn: async ({ channel_uuid, ...data }) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "PUT",
                    url: `/v1/chat/channels/${channel_uuid}`,
                    data,
                });
                const shape = makeZodResponse(channelSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: null };
                return { data: validated.data.result };
            },
        }),

        deleteChannelN: builder.mutation<boolean, { channel_uuid: string }>({
            queryFn: async ({ channel_uuid }) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "DELETE",
                    url: `/v1/chat/channels/${channel_uuid}`,
                });
                const shape = makeZodResponse(messageStatusResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: false };
                return { data: validated.data.result.success };
            },
        }),

        channelActionN: builder.mutation<boolean, ChannelAction>({
            queryFn: async ({ channel_uuid, action, users }) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "POST",
                    url: `/v1/chat/channels/${channel_uuid}/${action}`,
                    data: users ? { users } : undefined,
                });
                const shape = makeZodResponse(messageStatusResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: false };
                return { data: validated.data.result.success };
            },
        }),

        getChatMembers: builder.query<ChatMember[], { group_type: string, chat_uuid: string }>({
            providesTags: ['nebula_chat'],
            queryFn: async ({ group_type, chat_uuid }) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "GET",
                    url: `/v1/chat/members/${group_type}/${chat_uuid}`,
                });
                const validated = z.array(chatMemberSchema).safeParse(result.data);

                if (!validated.success) return { data: [] };
                return { data: validated.data };
            },
        }),

        getChatUserDetails: builder.query<Chatter, { user_uuid: string }>({
            providesTags: ['nebula_chat'],
            query: ({ user_uuid }) => ({
                method: 'GET',
                url: '/api/get-chat-user-details',
                params: { uuid: user_uuid }
            }),
            transformResponse: ({ result }: { result: unknown }) => {
                const v = chatterSchema.safeParse(result);
                if (!v.success) {
                    console.error(v.error);
                    return {
                        uuid: "Unknown",
                        name: "Unknown User",
                        is_active: false,
                        file_key: null,
                    }
                }
                return v.data;
            }
        }),

        getChatIntegrationDetails: builder.query<IntegrationDestination[], { integrations: string[], voip_user_uuid: string }>({
            providesTags: ['nebula_chat'],
            query: ({ integrations, voip_user_uuid }) => ({
                method: 'POST',
                url: '/api/get-chat-integration-details',
                params: { voip_user_uuid },
                data: { integrations }
            }),
            transformResponse: ({ result }: { result: unknown }) => {
                const validated = z.array(integrationDataSchema).safeParse(result);

                if (!validated.success) {
                    console.error(validated.error);
                    return []
                }
                return validated.data;
            }
        }),

        getChatFriendDetails: builder.query<Chatter[], { voip_user_uuid: string }>({
            providesTags: ['nebula_chat'],
            query: ({ voip_user_uuid }) => ({
                method: 'GET',
                url: '/api/get-chat-friends',
                params: { voip_user_uuid }
            }),
            transformResponse: ({ result }: { result: unknown }) => {
                const v = z.array(chatterSchema).safeParse(result);
                if (!v.success) {
                    console.error(v.error);
                    return []
                }

                return v.data;
            }
        }),

        // Channel Avatar
        getChannelAvatarN: builder.query<string, { channel_uuid: string }>({
            providesTags: ['nebula_chat'],
            queryFn: async ({ channel_uuid }) =>
                nebulaFetchBlob({ url: `/v1/chat/channels/${channel_uuid}/avatar` }),
        }),

        putChannelAvatarN: builder.mutation<boolean, { channel_uuid: string; data: ArrayBuffer }>({
            queryFn: async ({ channel_uuid, data }) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "PUT",
                    url: `/v1/chat/channels/${channel_uuid}/avatar`,
                    data,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                const shape = makeZodResponse(messageStatusResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: false };
                return { data: validated.data.result.success };
            },
        }),

        deleteChannelAvatarN: builder.mutation<boolean, { channel_uuid: string }>({
            queryFn: async ({ channel_uuid }) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "DELETE",
                    url: `/v1/chat/channels/${channel_uuid}/avatar`,
                });
                const shape = makeZodResponse(messageStatusResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: false };
                return { data: validated.data.result.success };
            },
        }),

        // Messages mark
        getMessagesN: builder.query<
            { messages: ChatMessage[]; has_more: boolean },
            NebulaMessagesRequest
        >({
            providesTags: ['nebula_chat'],
            queryFn: async ({ uuid, type, limit, message_ref }) => {
                const { before, after } = cursor_from_message_ref(message_ref);

                const result = await nebulaFetch<{ result: unknown }>({
                    method: "GET",
                    url: "/v1/chat/messages",
                    params: { uuid, type, limit, before, after },
                });

                const validated = getMessagesResponseSchema.safeParse(result.data);

                if (!validated.success) return { data: { messages: [], has_more: false } };
                return { data: validated.data };
            },
        }),

        getMessageN: builder.query<ChatMessage | null, string>({
            providesTags: ['nebula_chat'],
            queryFn: async (message_uuid) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "GET",
                    url: `/v1/chat/messages/${message_uuid}`,
                });
                const validated = getMessageResponseSchema.safeParse(result.data);

                if (!validated.success) return { data: null };
                return { data: validated.data.message };
            },
        }),

        sendMessageN: builder.mutation<
            { message: ChatMessage; pending_files?: PendingFileInfo[] | null },
            SendMessageRequest
        >({
            queryFn: async (data) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "POST",
                    url: "/v1/chat/messages",
                    data,
                });
                const validated = sendMessageResponseSchema.safeParse(result.data);

                if (!validated.success) return { error: {} };
                return { data: validated.data };
            },
        }),

        deleteMessageN: builder.mutation<boolean, string>({
            queryFn: async (message_uuid) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "DELETE",
                    url: `/v1/chat/messages/${message_uuid}`,
                });
                const shape = makeZodResponse(messageStatusResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: false };
                return { data: validated.data.result.success };
            },
        }),

        updateMessageStatusN: builder.mutation<boolean, { message_uuid: string; status: MessageStatus }>({
            queryFn: async ({ message_uuid, status }) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "PUT",
                    url: `/v1/chat/messages/${message_uuid}/${status}`,
                });
                const shape = makeZodResponse(messageStatusResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: false };
                return { data: validated.data.result.success };
            },
        }),

        readAllMessagesN: builder.mutation<boolean, { uuid: string; type: ChatGroupType }>({
            queryFn: async ({ uuid, type }) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "POST",
                    url: "/v1/chat/messages_action/read_all",
                    params: { uuid, type },
                });
                const shape = makeZodResponse(readAllResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: false };
                return { data: validated.data.result.success };
            },
        }),

        // Last Messages / Conversations
        getLastMessagesN: builder.query<
            { conversations: Conversation[]; has_more: boolean; next_cursor?: string | null },
            { limit?: number; before?: string; after?: string; type?: string[] }
        >({
            providesTags: ['conversation_list', 'nebula_chat'],
            queryFn: async ({ limit, after, type }) => {
                const messagesResult = await nebulaFetch<{ result: unknown }>({
                    method: "GET",
                    url: "/v1/chat/last_messages",
                    params: {
                        limit, type, after
                    },
                    // params: { limit, before, since, type },
                });
                const validatedMessages = getLastMessagesResponseSchema.safeParse(messagesResult.data);

                if (!validatedMessages.success) return { data: { conversations: [], has_more: false } };

                return { data: validatedMessages.data };
            },
            async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
                try {
                    const { data } = await queryFulfilled;

                    const state = getState();

                    const voip_user_uuid = selectCurrentUserId(state);
                    const activeChat = selectActiveConversation(state as any);

                    const integrations: string[] = []

                    data.conversations.forEach(c => {
                        if (c.type === 'integration' && c.integration) {
                            integrations.push(c.integration.uuid);
                        }
                        if (activeChat?.uuid && c.uuid === activeChat.uuid) {
                            dispatch(setActiveChat(''));
                        }
                    });

                    if (integrations.length > 0) {
                        dispatch(nebulaChatApi.endpoints.getChatIntegrationDetails.initiate({ integrations, voip_user_uuid }));
                    }
                } catch {
                    // query failed, nothing to do
                }
            },
        }),

        // Unread
        // getMessagesUnreadN: builder.query<{ count: number; first_unread?: { uuid: string; time: string } | null }, { uuid: string; type: ChatGroupType }>({
        //     queryFn: async ({ uuid, type }) => {
        //         const result = await nebulaFetch<{ result: unknown }>({
        //             method: "GET",
        //             url: "/v1/chat/messages_unread",
        //             params: { uuid, type },
        //         });
        //         const shape = makeZodResponse(getUnreadResponseSchema);
        //         const validated = shape.safeParse(result.data);
        //         if (!validated.success) return { data: { count: 0 } };
        //         return { data: validated.data.result };
        //     },
        // }),

        // Starred
        getMessagesStarredN: builder.query<ChatMessage[], { uuid: string; type: ChatGroupType }>({
            providesTags: ['nebula_chat'],
            queryFn: async ({ uuid, type }) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "GET",
                    url: "/v1/chat/messages_starred",
                    params: { uuid, type },
                });
                const shape = makeZodResponse(getStarredResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: [] };
                return { data: validated.data.result.messages };
            },
        }),

        // Search
        searchMessagesN: builder.query<
            ChatMessage[],
            { pattern: string; to: string; to_type: string; before?: string; after?: string; limit?: number; } |
            { pattern: string; to?: never; to_type?: never; before?: string; after?: string; limit?: number; }
        >({
            providesTags: ['nebula_chat'],
            queryFn: async (params) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "GET",
                    url: "/v1/chat/messages_search",
                    params,
                });
                const validated = searchMessagesResponseSchema.safeParse(result.data);

                if (!validated.success) return { data: [] };
                return { data: validated.data.messages };
            },
        }),

        // // Friends
        // getFriendsN: builder.query<Friend[], void>({
        //     queryFn: async () => {
        //         const result = await nebulaFetch<{ result: unknown }>({
        //             method: "GET",
        //             url: "/v1/chat/friends",
        //         });
        //         const shape = makeZodResponse(getFriendsResponseSchema);
        //         const validated = shape.safeParse(result.data);
        //         if (!validated.success) return { data: [] };
        //         return { data: validated.data.result.friends };
        //     },
        // }),

        // getFriendsDataN: builder.query<any, void>({
        //     queryFn: async () => {
        //         const result = await nebulaFetch<{ result: unknown }>({
        //             method: "GET",
        //             url: "/v1/chat/friends_uuids",
        //         });

        //         const validateFriendUuids = z.array(z.string()).safeParse(result.data);

        //         if (!validateFriendUuids.success) {
        //             return { data: {} };
        //         }

        //         return { data: [] }
        //     },
        // }),

        // getFriendN: builder.query<Friend | null, string>({
        //     queryFn: async (friend_uuid) => {
        //         const result = await nebulaFetch<{ result: unknown }>({
        //             method: "GET",
        //             url: `/v1/chat/friends/${friend_uuid}`,
        //         });
        //         const shape = makeZodResponse(friendSchema);
        //         const validated = shape.safeParse(result.data);
        //         if (!validated.success) return { data: null };
        //         return { data: validated.data.result };
        //     },
        // }),

        // deleteFriendN: builder.mutation<boolean, string>({
        //     queryFn: async (friend_uuid) => {
        //         const result = await nebulaFetch<{ result: unknown }>({
        //             method: "DELETE",
        //             url: `/v1/chat/friends/${friend_uuid}`,
        //         });
        //         const shape = makeZodResponse(messageStatusResponseSchema);
        //         const validated = shape.safeParse(result.data);
        //         if (!validated.success) return { data: false };
        //         return { data: validated.data.result.success };
        //     },
        // }),

        // // Friend Requests
        // getFriendRequestsN: builder.query<FriendRequest[], void>({
        //     queryFn: async () => {
        //         const result = await nebulaFetch<{ result: unknown }>({
        //             method: "GET",
        //             url: "/v1/chat/friend_requests",
        //         });
        //         const shape = makeZodResponse(getFriendRequestsResponseSchema);
        //         const validated = shape.safeParse(result.data);
        //         if (!validated.success) return { data: [] };
        //         return { data: validated.data.result.requests };
        //     },
        // }),

        // createFriendRequestN: builder.mutation<FriendRequest | null, { to: string; message?: string }>({
        //     queryFn: async (data) => {
        //         const result = await nebulaFetch<{ result: unknown }>({
        //             method: "POST",
        //             url: "/v1/chat/friend_requests",
        //             data,
        //         });
        //         const shape = makeZodResponse(friendRequestSchema);
        //         const validated = shape.safeParse(result.data);
        //         if (!validated.success) return { data: null };
        //         return { data: validated.data.result };
        //     },
        // }),

        // respondFriendRequestN: builder.mutation<boolean, { friend_uuid: string; action: "accept" | "reject" }>({
        //     queryFn: async ({ friend_uuid, action }) => {
        //         const result = await nebulaFetch<{ result: unknown }>({
        //             method: "POST",
        //             url: `/v1/chat/friend_requests/${friend_uuid}/${action}`,
        //         });
        //         const shape = makeZodResponse(messageStatusResponseSchema);
        //         const validated = shape.safeParse(result.data);
        //         if (!validated.success) return { data: false };
        //         return { data: validated.data.result.success };
        //     },
        // }),

        // Files
        getFilesN: builder.query<
            { files: FileResponse[]; has_more: boolean; next_cursor?: string | null },
            { type: string; uuid: string; limit?: number; before?: string; after?: string }
        >({
            providesTags: ['nebula_chat'],
            queryFn: async (params) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "GET",
                    url: "/v1/chat/files",
                    params,
                });
                const shape = makeZodResponse(getFilesResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: { files: [], has_more: false } };
                return { data: validated.data.result };
            },
        }),

        pullFileN: builder.query<string | null, { file_uuid: string; thumbnail?: boolean; contentType?: string }>({
            providesTags: ['nebula_chat'],
            queryFn: async ({ file_uuid, thumbnail, contentType }) =>
                nebulaFetchBlob({
                    url: `/v1/chat/files/${file_uuid}`,
                    params: { download: true, thumbnail },
                    contentType,
                })
        }),

        uploadFileN: builder.mutation<UploadFileResponse | null, { file_uuid: string; data: ArrayBuffer }>({
            queryFn: async ({ file_uuid, data }) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "PUT",
                    url: `/v1/chat/files/${file_uuid}`,
                    data,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                const shape = makeZodResponse(uploadFileResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: null };
                return { data: validated.data.result };
            },
        }),

        sendMessageReaction: builder.mutation<
            boolean,
            SendMessageReactionParams
        >({
            queryFn: async params => {
                if (!SOCKET) {
                    return { error: "No Socket" };
                }
                try {
                    await SOCKET.sendReaction(params);
                    return { data: true };
                } catch (error: any) {
                    return { error: error.message };
                }
            },
        }),
        deleteMessageReaction: builder.mutation<
            boolean,
            DeleteMessageReactionsParams
        >({
            queryFn: async params => {
                if (!SOCKET) {
                    return { error: "No Socket" };
                }
                try {
                    await SOCKET.deleteReaction(params);
                    return { data: true };
                } catch (error: any) {
                    return { error: error.message };
                }
            },
        }),

        // Threads
        getThreadCountN: builder.query<{ message_uuid: string; count: number }, string>({
            providesTags: ['nebula_chat'],
            queryFn: async (message_uuid) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "GET",
                    url: `/v1/chat/threads/${message_uuid}/count`,
                });
                const shape = makeZodResponse(threadCountResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: { message_uuid: "", count: 0 } };
                return { data: validated.data.result };
            },
        }),

        getThreadLastMessageN: builder.query<{ message_uuid: string; last_message?: ChatMessage | null }, string>({
            providesTags: ['nebula_chat'],
            queryFn: async (message_uuid) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "GET",
                    url: `/v1/chat/threads/${message_uuid}/last`,
                });

                const shape = makeZodResponse(threadLastMessageResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: { message_uuid: "" } };
                return { data: validated.data.result };
            },
        }),

        getThreadMembersN: builder.query<{ message_uuid: string; members: ThreadMember[] }, string>({
            providesTags: ['nebula_chat'],
            queryFn: async (message_uuid) => {
                const result = await nebulaFetch<{ result: unknown }>({
                    method: "GET",
                    url: `/v1/chat/threads/${message_uuid}/members`,
                });
                const shape = makeZodResponse(getThreadMembersResponseSchema);
                const validated = shape.safeParse(result.data);
                if (!validated.success) return { data: { message_uuid: "", members: [] } };
                return { data: validated.data.result };
            },
        }),

        sendTypingStatusN: builder.mutation<
            boolean,
            UserTyping
        >({
            queryFn: async report => {
                if (!SOCKET) {
                    return { error: "No Socket" };
                }
                try {
                    await SOCKET.notifyTyping(report);
                    return { data: true };
                } catch (error: any) {
                    return { error: error.message };
                }
            },
        }),
        updateChatMember: builder.mutation<
            boolean,
            ChatUserUpdated
        >({
            queryFn: async userUpdate => {
                if (!SOCKET) {
                    return { error: "No Socket" };
                }
                try {
                    await SOCKET.updateChatMember(userUpdate);
                    return { data: true };
                } catch (error: any) {
                    return { error: error.message };
                }
            },
        }),
        markChatRead: builder.mutation<
            boolean,
            { to: string, to_type: string, is_read: boolean }
        >({
            queryFn: async ({ to, to_type, is_read }) => {
                if (!SOCKET) {
                    return { error: "No Socket" };
                }
                try {
                    await SOCKET.updateChatReadStatus(to, to_type, is_read);
                    return { data: true };
                } catch (error: any) {
                    return { error: error.message };
                }
            },
        }),
    }),
});
